import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Horizontally scrollable recommendation slider used inside the add-to-cart dialog.
 *
 * Adds previous/next arrow controls on top of the native scroll-snap list. Arrows are only
 * shown when the content overflows, and are disabled when the list reaches either edge.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The scrollable list of recommendation cards.
 * @property {HTMLButtonElement} [previous] - The previous (scroll left) arrow.
 * @property {HTMLButtonElement} [next] - The next (scroll right) arrow.
 *
 * @extends Component<Refs>
 */
class AddToCartCollectionComponent extends Component {
  requiredRefs = ['list'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.refs.list.addEventListener('scroll', this.#update, { passive: true });

    // The slider lives inside a <dialog> that is display:none until opened, so it has no measurable
    // size on connect. Observe size changes to recompute once the dialog becomes visible.
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.refs.list);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.list?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  scrollPrevious() {
    this.#scrollByAmount(-1);
  }

  scrollNext() {
    this.#scrollByAmount(1);
  }

  /**
   * Scrolls the list by roughly one card (item width + gap) in the given direction.
   *
   * @param {number} direction - `-1` to scroll left, `1` to scroll right.
   */
  #scrollByAmount(direction) {
    const { list } = this.refs;
    if (!list) return;

    const item = list.querySelector('.add-to-cart-collection__item');
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const amount = item instanceof HTMLElement ? item.offsetWidth + gap : list.clientWidth * 0.8;

    list.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  /**
   * Toggles arrow visibility based on overflow and updates the disabled state at each edge.
   */
  #update = debounce(() => {
    const { list, previous, next } = this.refs;
    if (!list) return;

    const maxScroll = list.scrollWidth - list.clientWidth;
    const overflowing = maxScroll > 1;

    this.dataset.overflowing = overflowing ? 'true' : 'false';

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = !overflowing || list.scrollLeft <= 1;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = !overflowing || list.scrollLeft >= maxScroll - 1;
    }
  }, 50);
}

if (!customElements.get('add-to-cart-collection-component')) {
  customElements.define('add-to-cart-collection-component', AddToCartCollectionComponent);
}
